import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useList } from '@refinedev/core';
import type { VendorConnectStatus } from 'shared';
import { Avatar } from '../../../components/Avatar.js';
import { useCan } from '../../../lib/capabilities.js';

export interface VendorRow {
  id: string;
  name: string;
  trade: string | null;
  email: string | null;
  phone: string | null;
  hasPortalAccount: boolean;
  connect: { status: VendorConnectStatus };
  archivedAt: string | null;
}

function VendorCard({ row, onOpen }: { row: VendorRow; onOpen: () => void }) {
  return (
    <div
      className="person-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <Avatar name={row.name} photoUrl={null} size="md" />
      <div className="person-card__body">
        <span>
          <strong>{row.name}</strong>
          {row.archivedAt ? (
            <>
              {' '}
              <span className="pill">Archived</span>
            </>
          ) : null}
        </span>
        <span className="muted">{row.email ?? 'No email on file'}</span>
        <div className="person-card__meta">
          <span className="muted">{row.trade ?? 'No trade on file'}</span>
          {row.hasPortalAccount ? (
            <span className="pill">Has login</span>
          ) : (
            <span className="muted">No login</span>
          )}
          {/* Same "only the two states worth a glance" rule OwnerList.tsx
              already uses — see its own comment. */}
          {row.connect.status === 'ENROLLED' ? (
            <span className="pill pill--positive">Direct deposit</span>
          ) : row.connect.status === 'REQUIREMENTS_DUE' ? (
            <span className="pill pill--negative">Payout action needed</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function VendorList() {
  const navigate = useNavigate();
  const canWrite = useCan('maintenance:write');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const pageSize = 25;

  const { data, isLoading, error } = useList<VendorRow>({
    resource: 'vendors',
    pagination: { current: page, pageSize },
    filters: [
      { field: 'search', operator: 'contains', value: search || undefined },
      { field: 'includeArchived', operator: 'eq', value: includeArchived ? 'true' : undefined },
    ],
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section>
      <div className="row">
        <h1>Vendors</h1>
        <span className="shell__spacer" />
        {canWrite ? (
          <button
            type="button"
            className="button"
            onClick={() => void navigate('/staff/vendors/new')}
          >
            Add vendor
          </button>
        ) : null}
      </div>

      <div className="row">
        <input
          className="field__input"
          type="search"
          placeholder="Search name or trade"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />
        <label className="row">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => {
              setIncludeArchived(event.target.checked);
              setPage(1);
            }}
          />
          <span className="muted">Show archived</span>
        </label>
      </div>

      {error ? (
        <p className="alert alert--error">
          {error instanceof Error ? error.message : 'Could not load this list.'}
        </p>
      ) : isLoading ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">{search ? `No vendors match “${search}”.` : 'No vendors yet.'}</p>
      ) : (
        <div className="person-grid">
          {rows.map((row) => (
            <VendorCard
              key={row.id}
              row={row}
              onOpen={() => void navigate(`/staff/vendors/${row.id}/edit`)}
            />
          ))}
        </div>
      )}

      {total > pageSize ? (
        <div className="row">
          <button
            type="button"
            className="button button--quiet"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            ← Previous
          </button>
          <span className="muted">
            Page {page} of {lastPage} · {total} total
          </span>
          <button
            type="button"
            className="button button--quiet"
            disabled={page >= lastPage}
            onClick={() => setPage(page + 1)}
          >
            Next →
          </button>
        </div>
      ) : null}
    </section>
  );
}
